import * as React from 'react';
import {
  type SharedOptions,
  type FeatureFlagName,
  isTestEnv,
  DEFAULT_FALSE_FLAGS,
} from '@packages/systems/feature-config/shared';
import {FeatureConfigContext} from '../context';
import type {FlagMap} from '../context';
import {notifyObservers} from '../observers';
import {overrideAdapter} from '../overrideAdapter';
import {getStatsigClient, statsigClientExists} from '../client';

const STATSIG_OVERRIDE_REASONS = ['LocalOverride:Recognized'];

export function OverrideFeatureFlags({
  children,
  flags,
}: React.PropsWithChildren<{
  flags: FlagMap;
}>) {
  const context = React.useContext(FeatureConfigContext);

  const value = React.useMemo(
    () => ({
      ...context,
      overrides: {...context.overrides, ...flags},
    }),
    [context, flags]
  );

  return (
    <FeatureConfigContext.Provider value={value}>
      {children}
    </FeatureConfigContext.Provider>
  );
}

const defaultFlagOptions = {shouldLogExposure: true};

export const getFeatureFlagOutsideOfReact = (
  flag: FeatureFlagName,
  flagOptions: SharedOptions = defaultFlagOptions
): boolean => {
  if (statsigClientExists()) {
    const gateValue = getStatsigClient().getFeatureGate(flag.toLowerCase(), {
      disableExposureLog: !flagOptions.shouldLogExposure,
    });

    /*
     * In test env, if the flag is not an override, default to true unless in DEFAULT_FALSE_FLAGS.
     *
     * Since the overrides on the client side are integrated with the Statsig SDK, we have to use the evaluation details in order to tell if this flag's value is
     * coming from an override or from the default value.
     */
    if (isTestEnv()) {
      // If the reason is an override, return the value as normal.
      if (
        gateValue?.details?.reason &&
        STATSIG_OVERRIDE_REASONS.includes(gateValue?.details?.reason)
      ) {
        return gateValue?.value ?? false;
      }

      // If the reason is not an override, default to true unless in DEFAULT_FALSE_FLAGS.
      return !DEFAULT_FALSE_FLAGS.includes(flag.toLowerCase());
    }

    return gateValue?.value ?? false;
  }

  // If the Statsig client doesn't exist, and we're in the test environment, we
  // still want to exhibit the expected test environment behavior.
  if (isTestEnv()) {
    return !DEFAULT_FALSE_FLAGS.includes(flag.toLowerCase());
  }

  return false;
};

export const setFeatureFlag = (flag: FeatureFlagName, value: boolean) => {
  if (statsigClientExists()) {
    overrideAdapter.overrideGate(flag.toLowerCase(), value);
    notifyObservers();
  }
};

export const resetFeatureFlag = (flag: FeatureFlagName) => {
  if (statsigClientExists()) {
    overrideAdapter.removeGateOverride(flag.toLowerCase());
    notifyObservers();
  }
};

export const useFeatureFlag = (
  flag: FeatureFlagName,
  flagOptions: SharedOptions = defaultFlagOptions
): boolean => {
  const {overrides} = React.useContext(FeatureConfigContext);

  if (overrides.hasOwnProperty(flag)) {
    return Boolean(overrides[flag]);
  }

  return getFeatureFlagOutsideOfReact(flag, flagOptions);
};

export const useFeatureFlags = (
  flags: string[],
  flagOptions: SharedOptions = defaultFlagOptions
): {[ff_name: string]: boolean} => {
  const {overrides} = React.useContext(FeatureConfigContext);

  return React.useMemo(
    () =>
      Object.assign(
        {},
        ...flags.map((flag) => {
          const val = overrides.hasOwnProperty(flag)
            ? Boolean(overrides[flag])
            : getFeatureFlagOutsideOfReact(flag, flagOptions);
          return {[flag]: val};
        })
      ),
    [flags, overrides, flagOptions]
  );
};

export const FeatureFlagSwitch = ({
  children,
  flag,
  shouldLogExposure,
}: {
  children: React.ReactNode | ((isEnabled: boolean) => React.ReactNode);
  flag: FeatureFlagName;
} & Partial<SharedOptions>) => {
  const flagOptions: SharedOptions = React.useMemo(
    () => ({
      shouldLogExposure:
        shouldLogExposure ?? defaultFlagOptions.shouldLogExposure,
    }),
    [shouldLogExposure]
  );
  const value = useFeatureFlag(flag, flagOptions);

  return typeof children === 'function'
    ? children(value)
    : value
      ? children
      : null;
};

// Returns a list of flags that are enabled, but will evaluate the flags without
// logging an exposure. This is useful for checking if any flags are enabled but
// not when the code path actually diverges.
export const getEnabledFlags = (flags: FeatureFlagName[]) => {
  if (!statsigClientExists()) return [];

  return flags.filter((flag: FeatureFlagName) =>
    getStatsigClient().checkGate(flag, {
      disableExposureLog: true,
    })
  );
};
